/**
 * Compose Email Dialog Component
 * Simple email composition modal for sending emails to leads/clients
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import {
  CloseOutlined,
  Send as SendIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { emailTemplateApi, emailSendApi } from '../api/emailCampaignApi';
import { toast } from 'react-toastify';

interface ComposeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  recipientName?: string;
  recipientEmail?: string;
  recipientCompany?: string;
  leadId?: string;
  clientId?: string;
}

const quickPrompts = [
  { label: 'Follow-up', prompt: 'Professional follow-up email after our conversation' },
  { label: 'Introduction', prompt: 'Introduction email presenting our services' },
  { label: 'Thank you', prompt: 'Thank you email for your time and interest' },
  { label: 'Meeting Request', prompt: 'Request to schedule a meeting or call' },
];

const ComposeEmailDialog: React.FC<ComposeEmailDialogProps> = ({
  open,
  onClose,
  onSuccess,
  recipientName,
  recipientEmail,
  recipientCompany,
  leadId,
  clientId,
}) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  // Reset form when dialog opens with new recipient
  useEffect(() => {
    if (open) {
      setTo(recipientEmail || '');
      setSubject('');
      setBody('');
      setAiPrompt('');
    }
  }, [open, recipientEmail]);

  const handleGenerateAI = async (prompt: string) => {
    setIsGenerating(true);
    try {
      const result = await emailTemplateApi.generateAITemplate(prompt, {
        category: 'FOLLOW_UP',
        recipientName: recipientName,
        recipientCompany: recipientCompany,
        tone: 'professional',
      });

      if (result.subject) {
        setSubject(result.subject);
      }
      if (result.htmlContent) {
        // Convert HTML to plain text
        const plainText = result.htmlContent
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n\n')
          .replace(/<li>/gi, '\n• ')
          .replace(/<\/li>/gi, '')
          .replace(/<\/h[1-6]>/gi, '\n\n')
          .replace(/<(ul|ol|p|div|h[1-6])[^>]*>/gi, '')
          .replace(/<\/(ul|ol|div)>/gi, '\n')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        setBody(plainText);
      }
      toast.success('Email generated successfully!');
    } catch (error: any) {
      console.error('Failed to generate email:', error);
      toast.error('Failed to generate email. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Please enter recipient email');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter email subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter email body');
      return;
    }

    setIsSending(true);
    try {
      // Convert plain text body to HTML (preserve line breaks)
      const htmlContent = body
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');

      // Send email via SMTP using backend API (includes leadId for activity logging)
      await emailSendApi.sendEmail({
        to: to.trim(),
        toName: recipientName,
        subject: subject.trim(),
        htmlContent: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${htmlContent}</div>`,
        textContent: body,
        leadId,
        clientId,
      });

      toast.success('Email sent successfully! 電郵已發送!');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to send email:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send email. Please check your email domain settings.';
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Compose Email 撰寫電郵
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Recipient Info */}
          {recipientName && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Sending to: <strong>{recipientName}</strong>
              {recipientCompany && ` (${recipientCompany})`}
            </Alert>
          )}

          {/* To Field */}
          <TextField
            label="To 收件人"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            fullWidth
            size="small"
            type="email"
          />

          {/* Subject Field */}
          <TextField
            label="Subject 主題"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
          />

          {/* AI Quick Prompts */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AIIcon fontSize="small" />
              AI Quick Generate AI快速生成:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {quickPrompts.map((qp) => (
                <Chip
                  key={qp.label}
                  label={qp.label}
                  size="small"
                  onClick={() => handleGenerateAI(qp.prompt)}
                  disabled={isGenerating}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>

          {/* Custom AI Prompt */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Or describe what you want AI to write..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., Write a polite email asking for a product demo"
            />
            <Button
              variant="outlined"
              onClick={() => aiPrompt && handleGenerateAI(aiPrompt)}
              disabled={!aiPrompt.trim() || isGenerating}
              startIcon={isGenerating ? <CircularProgress size={16} /> : <AIIcon />}
            >
              {isGenerating ? 'Generating...' : 'Generate'}
            </Button>
          </Box>

          {/* Body Field */}
          <TextField
            label="Message 內容"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            rows={12}
            placeholder="Write your email message here..."
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel 取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending || !to.trim() || !subject.trim() || !body.trim()}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {isSending ? 'Sending...' : 'Send 發送'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComposeEmailDialog;
